import { Fetcher, HTTPError } from '@this/src/util';
import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { OrganizationTitle } from '@this/components/organization/organization.style';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { useConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal';
import type { ExpensesTypeJson } from '@this/src/domain/expenses/expenses_type';
import ExpensesType from '@this/src/domain/expenses/expenses_type';
import type { ExpensesAccountTypeJson } from '@this/src/domain/expenses/expenses_account_type';
import ExpensesAccountType from '@this/src/domain/expenses/expenses_account_type';
import Cards from './cards';
import EditFormModal from './edit_form_modal';

type Props = {
  theme: { themeClass: string };
};

type IndexResponse = {
  expenses_types: ExpensesTypeJson[];
  expenses_account_types: ExpensesAccountTypeJson[];
  category_options: [string, string][];
};

interface ExpensesTypeResponse {
  expenses_type: ExpensesTypeJson;
  expenses_account_type: ExpensesAccountTypeJson;
}

const ExpensesTypes: React.FC<Props> = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | string[]>('');
  const [types, setTypes] = useState<ExpensesType[]>([]);
  const [accountTypes, setAccountTypes] = useState<ExpensesAccountType[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<[string, string][]>([]);
  const [editingType, setEditingType] = useState<ExpensesType | null>(null);
  const { open } = useConfirmModal();
  const [actionError, setActionError] = useState<string | string[] | null>(null);

  const getHttpErrorMessage = (e: unknown) => {
    if (e instanceof HTTPError && e.response?.data) {
      const responseError = e.response.data.error ?? e.response.data.errors ?? e.response.data.message;
      if (Array.isArray(responseError)) {
        return responseError;
      }
      if (responseError) {
        return responseError;
      }
    }

    return '通信エラーが発生しました。時間をおいて再度お試しください。';
  };

  const fetchTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await Fetcher.get<IndexResponse>('/organization/expenses_types.json');
      const expensesTypes = response.expenses_types.map(type => new ExpensesType(type));
      const expensesAccountTypes = response.expenses_account_types.map(
        account => new ExpensesAccountType(account)
      );
      setTypes(expensesTypes);
      setActionError(null);
      setAccountTypes(expensesAccountTypes);
      setCategoryOptions(response.category_options);
    } catch (e) {
      setError(getHttpErrorMessage(e));
    }
    setLoading(false);
  };

  const deleteType = async (target: ExpensesType) => {
    try {
      await Fetcher.delete(`/organization/expenses_types/${target.id}`, {});
      fetchTypes();
    } catch (e) {
      setActionError(getHttpErrorMessage(e));
    }
  };

  const clickEdit = async (t: ExpensesType | null) => {
    if (t !== null) {
      setActionError(null);
      const editT = _.cloneDeep(t);
      setEditingType(editT);
    }
  };

  const clickAdd = async () => {
    setActionError(null);
    try {
      const response = await Fetcher.get<ExpensesTypeResponse>(`/organization/expenses_types/new`);
      const new_type = new ExpensesType(response.expenses_type);
      setTypes([...types, new_type]);
      setEditingType(new_type);
    } catch (e) {
      setError(getHttpErrorMessage(e));
    }
  };

  const clickCancel = () => {
    if (editingType && !editingType.id && types) {
      setTypes(types.filter(t => t.id));
    }
    setEditingType(null);
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const modal = () => (
    <>
      {editingType && (
        <EditFormModal
          type={editingType}
          accountTypes={accountTypes}
          categoryOptions={categoryOptions}
          onSubmit={() => {
            setEditingType(null);
            fetchTypes();
          }}
          hideModal={clickCancel}
        />
      )}
    </>
  );

  return (
    <>
      <OrganizationTitle>経費科目マスタ</OrganizationTitle>
      {loading ? (
        <Loading />
      ) : error ? (
        <div>{Array.isArray(error) ? error.map((message, index) => <div key={index}>{message}</div>) : error}</div>
      ) : (
        <div style={{ padding: 10 }}>
          {actionError && (
            <Alert variant="danger" style={{ marginBottom: '10px' }}>
              {Array.isArray(actionError)
                ? actionError.map(message => <div key={message}>{message}</div>)
                : actionError}
            </Alert>
          )}
          <Cards
            types={types}
            accountTypes={accountTypes}
            onClickEdit={t => clickEdit(t)}
            onClickDelete={async t => {
              setActionError(null);
              const { result } = await open({
                description: [`${t.name + t.code}を削除してよろしいですか？`, '※この操作は取り消せません'],
                color: 'danger',
                submitButtonLabel: '削除',
                className: 'e2e-confirm-delete'
              });
              if (result) {
                await deleteType(t);
              }
            }}
          />
        </div>
      )}
      {!editingType && (
        <div style={{ padding: 10 }}>
          <Button onClick={clickAdd}>経費科目を追加</Button>
        </div>
      )}
      {modal()}
    </>
  );
};

export default ExpensesTypes;
